import { Button } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { BsExclamationTriangle } from "react-icons/bs";
import AdsSkeleton from "./AdsSkeleton";
import AdsControls from "./AdsControls";
import AdsSection from "./AdsSection";
import EmptyAdsState from "./EmptyAdsState";
import { PAGE_SIZE, useHomeAds } from "../hooks/useHomeAds";
import './homStyle.css';
import Pagination from "../../../components/shared/Pagination";

const SECTION_TITLES = {
    all: "Toutes les annonces",
    premium: "Articles Premium",
    verified: "Annonces Vérifiées",
} as const;

// Position saved by AdsCard when an ad was opened from this page
const readSavedScroll = (): { path: string; y: number } | null => {
    try {
        const raw = sessionStorage.getItem('adsScrollPosition');
        const saved = raw ? JSON.parse(raw) : null;
        return saved && typeof saved.path === 'string' && typeof saved.y === 'number' ? saved : null;
    } catch {
        return null;
    }
};

const HomeAds: React.FC = () => {
    const {
        // State
        ads,
        isLoading,
        fetchFailed,
        sort,
        displayAds,
        favorites,
        isTogglingFavorite,
        activeTab,
        currentPage,
        totalPages,
        totalItems,

        // Actions
        setSort,
        setDisplayAds,
        setActiveTab,
        toggleFavorite,
        handlePageChange,
        retryFetch,
    } = useHomeAds();

    // Coming back from an ad: once that page's ads are on screen, return to the exact
    // spot. Things above the list (categories, slider) may still be settling, so try
    // again for a moment until the page is tall enough to reach it.
    const [savedScroll] = useState(readSavedScroll);
    const scrollHandledRef = useRef(false);
    useEffect(() => {
        const saved = savedScroll;
        if (!saved || isLoading || scrollHandledRef.current) return;
        scrollHandledRef.current = true;
        try {
            sessionStorage.removeItem('adsScrollPosition');
        } catch {
            // storage unavailable: nothing to clear
        }
        if (saved.path !== window.location.pathname + window.location.search) return;

        let tries = 0;
        let timer: number | undefined;
        const restore = () => {
            window.scrollTo(0, saved.y);
            if (Math.abs(window.scrollY - saved.y) > 2 && ++tries < 20) {
                timer = window.setTimeout(restore, 100);
            }
        };
        restore();
        return () => window.clearTimeout(timer);
    }, [isLoading, savedScroll]);

    return (
        <div id="home-ads" className="max-w-full mx-auto">
            <AdsControls
                activeTab={activeTab}
                displayAds={displayAds}
                sort={sort}
                onTabChange={setActiveTab}
                onDisplayChange={setDisplayAds}
                onSortChange={setSort}
            />

            {isLoading ? (
                <AdsSkeleton displayType={displayAds} count={8} />
            ) : fetchFailed ? (
                <div className="bg-white p-8 rounded-lg shadow-sm flex flex-col items-center">
                    <BsExclamationTriangle className="text-amber-500 text-4xl mb-4" />
                    <h2 className="text-xl font-semibold mb-2">Annonces non disponibles</h2>
                    <p className="text-gray-600 text-center mb-4">
                        Nous n'avons pas pu charger les annonces. Veuillez réessayer.
                    </p>
                    <Button onClick={retryFetch} type="primary">
                        Réessayer
                    </Button>
                </div>
            ) : ads.length === 0 ? (
                activeTab === 'all'
                    ? <EmptyAdsState />
                    : <EmptyAdsState title="Aucune annonce pour le moment" description="Revenez bientôt : de nouvelles annonces sont publiées chaque jour." />
            ) : (
                <div>
                    <AdsSection
                        title={SECTION_TITLES[activeTab]}
                        ads={ads}
                        displayType={displayAds}
                        isPremium={activeTab === 'premium'}
                        isVerified={activeTab === 'verified'}
                        favorites={favorites}
                        isTogglingFavorite={isTogglingFavorite}
                        onToggleFavorite={toggleFavorite}
                    />

                    {totalPages > 1 && (
                        <div className="flex justify-center py-8 px-4">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                totalItems={totalItems}
                                pageSize={PAGE_SIZE}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HomeAds;
